import React, { useState, useEffect, useCallback } from "react";
import {
  MessageSquare,
  Search,
  ChevronLeft,
  ChevronRight,
  Clock,
  Hash,
  BarChart,
  RefreshCw,
  ArrowLeft,
  User,
  Bot,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { AUTH_URL } from "../../constants";

const ChannelBadge = ({ channel }) => {
  const colors = {
    widget: "bg-blue-100 text-blue-700",
    api: "bg-purple-100 text-purple-700",
    slack: "bg-yellow-100 text-yellow-700",
    sms: "bg-green-100 text-green-700",
    email: "bg-pink-100 text-pink-700",
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[channel] || "bg-slate-100 text-slate-600"}`}
    >
      {channel}
    </span>
  );
};

export default function ConversationHistory() {
  const { token } = useAuth();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchFacilities = useCallback(async () => {
    try {
      const res = await fetch(`${AUTH_URL}/my/facilities`, { headers });
      const data = await res.json();
      if (Array.isArray(data)) {
        setFacilities(data);
        if (data.length > 0 && !selectedFacility) {
          setSelectedFacility(data[0].facility_id);
        }
      }
    } catch (e) {
      console.error("Failed to fetch facilities:", e);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSessions = useCallback(async () => {
    if (!selectedFacility) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        facility_id: selectedFacility,
        page: String(page),
        per_page: "20",
      });
      if (search) params.set("search", search);

      const res = await fetch(`${AUTH_URL}/my/conversations?${params}`, {
        headers,
      });
      const data = await res.json();
      setSessions(data.sessions || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
    } catch {
      setSessions([]);
    }
    setLoading(false);
  }, [selectedFacility, page, search, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const openDetail = async (sessionId) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${AUTH_URL}/my/conversations/${sessionId}`, {
        headers,
      });
      const data = await res.json();
      setDetail(data);
    } catch {
      setDetail(null);
    }
    setDetailLoading(false);
  };

  if (detail) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setDetail(null)}
          className="flex items-center text-sm text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to conversations
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Session: {detail.session_id?.slice(0, 12)}...
            </h3>
            <span className="text-xs text-slate-500">
              {detail.messages?.length || 0} messages
            </span>
          </div>

          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {(detail.messages || []).map((msg, i) => (
              <div key={i} className="space-y-2">
                {/* User question */}
                <div className="flex items-start justify-end">
                  <div className="max-w-[70%]">
                    <div className="bg-blue-600 text-white rounded-xl rounded-br-sm px-4 py-2.5 text-sm">
                      {msg.user_question}
                    </div>
                    <div className="flex items-center justify-end mt-1 text-xs text-slate-400">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(msg.created_at).toLocaleString()}
                    </div>
                  </div>
                  <User className="w-6 h-6 text-blue-500 ml-2 flex-shrink-0 mt-1" />
                </div>

                {/* AI response */}
                {msg.ai_response && (
                  <div className="flex items-start">
                    <Bot className="w-6 h-6 text-slate-400 mr-2 flex-shrink-0 mt-1" />
                    <div className="max-w-[70%]">
                      <div className="bg-slate-100 text-slate-800 rounded-xl rounded-bl-sm px-4 py-2.5 text-sm">
                        {msg.ai_response}
                      </div>
                      <div className="flex items-center mt-1 text-xs text-slate-400 gap-3">
                        {msg.similarity_score != null && (
                          <span>
                            Relevance: {(msg.similarity_score * 100).toFixed(0)}
                            %
                          </span>
                        )}
                        {msg.tokens_input != null && (
                          <span>
                            {msg.tokens_input + (msg.tokens_output || 0)} tokens
                          </span>
                        )}
                        {msg.response_time_ms != null && (
                          <span>{msg.response_time_ms}ms</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Conversation History
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Browse and search past conversations
            </p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={selectedFacility}
              onChange={(e) => {
                setSelectedFacility(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              {facilities.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>
                  {f.facility_name || f.display_name || f.facility_id}
                </option>
              ))}
            </select>

            <form onSubmit={handleSearch} className="flex">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search conversations..."
                  className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm w-60"
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Session List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
            <span className="ml-2 text-slate-500 text-sm">Loading...</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <MessageSquare className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No conversations found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-slate-100">
              {sessions.map((s) => (
                <button
                  key={s.session_id}
                  onClick={() => openDetail(s.session_id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <MessageSquare className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {s.session_id?.slice(0, 16)}...
                        </span>
                        <ChannelBadge channel={s.channel} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {new Date(s.started_at).toLocaleString()}
                        </span>
                        <span className="flex items-center">
                          <Hash className="w-3 h-3 mr-1" />
                          {s.message_count} messages
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0">
                    {s.avg_relevance != null && (
                      <div className="text-right">
                        <div className="flex items-center text-xs text-slate-500">
                          <BarChart className="w-3 h-3 mr-1" />
                          {(s.avg_relevance * 100).toFixed(0)}% relevance
                        </div>
                      </div>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </button>
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200">
                <span className="text-sm text-slate-500">
                  {total} sessions total
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded-lg border border-slate-300 disabled:opacity-50 hover:bg-slate-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-slate-700">
                    {page} / {pages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    disabled={page === pages}
                    className="p-1.5 rounded-lg border border-slate-300 disabled:opacity-50 hover:bg-slate-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
